import { RefObject, useMemo, useRef } from 'react';

import { infiniteQueryOptions, useInfiniteQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import flatMap from 'lodash/flatMap';
import uniqBy from 'lodash/uniqBy';

import { SortDirection } from '@cast/design-system';
import { TimeRange, QueryOptions } from '@cast/shared-types';
import { toNumOrZero } from '@cast/utils';

import { apiClient } from '@cast/infrastructure-api';

const DBO_CACHE_QUERIES_QUERY_KEY = 'dbo-cache-queries';

export const getCacheQueriesQueryKey = (params?: {
  cacheGroupId: string;
  cacheId: string;
  sortDir?: SortDirection;
  sortBy?: string;
  queryContains?: string;
  key: string;
}) => {
  if (!params) return [DBO_CACHE_QUERIES_QUERY_KEY] as const;
  return [
    DBO_CACHE_QUERIES_QUERY_KEY,
    params.cacheGroupId,
    params.cacheId,
    params.sortDir,
    params.sortBy,
    params.queryContains,
    params.key,
  ] as const;
};

type UseCacheQueriesQueryArgs = Pick<QueryOptions, 'enabled'> & {
  cacheGroupId?: string;
  cacheId?: string;
  limit?: string;
  sortDir?: SortDirection;
  sortBy?: string;
  queryContains?: string;
  key: string;
  getRange: () => TimeRange;
};

const getCacheQueriesInfiniteQueryOptions = (
  {
    cacheGroupId,
    cacheId,
    enabled = true,
    sortDir,
    sortBy,
    queryContains,
    key,
    getRange,
  }: UseCacheQueriesQueryArgs,
  cachedDate: RefObject<ReturnType<UseCacheQueriesQueryArgs['getRange']>>
) =>
  infiniteQueryOptions({
    queryKey: getCacheQueriesQueryKey({
      cacheGroupId: cacheGroupId!,
      cacheId: cacheId!,
      sortDir,
      sortBy,
      queryContains,
      key,
    }),
    queryFn: async ({ pageParam }) => {
      let { from, to } = getRange();
      if (toNumOrZero(pageParam) > 0) {
        // Use cached date range for subsequent pages, because offset-based pagination is prone to
        // data overlap if more data is pushed to the top of the stack after the initial request
        from = cachedDate.current.from;
        to = cachedDate.current.to;
      } else {
        // Ensure that the "to" date is not in the future
        to = dayjs(to).isSameOrAfter(dayjs()) ? dayjs().toISOString() : to;
        cachedDate.current = { from, to };
      }
      const { data } = await apiClient.dbo.getCacheQueries({
        groupId: cacheGroupId!,
        cacheId: cacheId!,
        pageLimit: '50',
        sortOrder: sortDir,
        sortField: sortBy,
        pageCursor: pageParam,
        startTime: from,
        endTime: to,
        queryContains,
      });
      return data;
    },
    enabled: enabled && !!cacheGroupId && !!cacheId,
    initialPageParam: '',
    refetchOnWindowFocus: false,
    getNextPageParam: (lastPage) => {
      return lastPage?.nextPage?.cursor || undefined;
    },
  });

export const useCacheQueriesQuery = (args: UseCacheQueriesQueryArgs) => {
  const cachedDate = useRef(args.getRange());

  const { data: queryData, ...rest } = useInfiniteQuery(
    getCacheQueriesInfiniteQueryOptions(args, cachedDate)
  );

  const { data, totalCount } = useMemo(() => {
    const pages = queryData?.pages;
    return {
      data: pages
        ? // TODO(Vytautas): some items come duplicated across pages due to the dynamic nature
          // of queries data. This is a temporary fix.
          uniqBy(
            flatMap(pages, ({ items }) => items || []),
            (item) => item.templateHash
          )
        : [],
      totalCount: toNumOrZero(pages?.[0]?.totalCount),
    };
  }, [queryData]);

  return { ...rest, data, totalCount };
};
